import React, { useState, useEffect } from 'react';
import { Heart, Activity, Pill, AlertTriangle, ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL || '';

export default function ClientHealthContext({ clientId }) {
  const [conditions, setConditions] = useState([]);
  const [riskData, setRiskData] = useState(null);
  const [bbSummary, setBbSummary] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    let live = true;

    async function load() {
      setLoading(true);
      try {
        const [condRes, riskRes, bbRes] = await Promise.all([
          fetch(`${BASE}/health-data/conditions/${clientId}`, { credentials: 'include' }).catch(() => null),
          fetch(`${BASE}/churn-prediction/score/${clientId}`, { credentials: 'include' }).catch(() => null),
          fetch(`${BASE}/bluebutton/summary/${clientId}`, { credentials: 'include' }).catch(() => null),
        ]);

        if (live && condRes?.ok) {
          const data = await condRes.json();
          setConditions(data.conditions || []);
        }
        if (live && riskRes?.ok) {
          const data = await riskRes.json();
          setRiskData(data.score || data);
        }
        if (live && bbRes?.ok) {
          const data = await bbRes.json();
          setBbSummary(data);
        }
      } catch (err) {
        console.error('Failed to load health context:', err);
      } finally {
        if (live) setLoading(false);
      }
    }

    load();
    return () => { live = false; };
  }, [clientId]);

  const activeConditions = conditions.filter(c => c.clinical_status === 'active');
  const riskScore = riskData?.finalScore || riskData?.score || null;
  const riskCategory = riskData?.riskCategory?.name || null;

  // Determine if there's meaningful health data to show
  const hasData = activeConditions.length > 0 || riskScore || bbSummary;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-2">
          <Heart size={15} className="text-slate-300" />
          <span className="text-xs text-slate-400">Loading health context...</span>
        </div>
      </div>
    );
  }

  if (!hasData) return null;

  const riskColor = riskScore >= 65 ? 'text-red-600 bg-red-50' : riskScore >= 40 ? 'text-amber-600 bg-amber-50' : 'text-emerald-600 bg-emerald-50';

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
      >
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Heart size={15} className="text-rose-500" />
          Health Context
          {riskScore && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${riskColor}`}>
              Risk: {riskScore}
            </span>
          )}
        </h3>
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="p-3 space-y-3">
          {/* Risk Score */}
          {riskScore && (
            <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg">
              <TrendingUp size={16} className={riskScore >= 65 ? 'text-red-500' : riskScore >= 40 ? 'text-amber-500' : 'text-emerald-500'} />
              <div className="flex-1">
                <div className="text-xs text-slate-500">Retention Risk Score</div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-slate-800">{riskScore}</span>
                  {riskCategory && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${riskColor}`}>
                      {riskCategory}
                    </span>
                  )}
                </div>
              </div>
              {/* Risk bar */}
              <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${riskScore >= 65 ? 'bg-red-500' : riskScore >= 40 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(riskScore, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Active Conditions */}
          {activeConditions.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Activity size={13} className="text-blue-500" />
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  Active Conditions ({activeConditions.length})
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {activeConditions.slice(0, 8).map((c, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 text-[11px] font-medium bg-blue-50 text-blue-700 rounded-md border border-blue-100"
                  >
                    {c.condition_name || c.code_display || 'Unknown'}
                  </span>
                ))}
                {activeConditions.length > 8 && (
                  <span className="px-2 py-1 text-[11px] text-slate-500">
                    +{activeConditions.length - 8} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Blue Button Summary highlights */}
          {bbSummary && (
            <div className="grid grid-cols-2 gap-2">
              {bbSummary.dual_eligible && (
                <div className="px-2.5 py-1.5 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="text-[10px] text-purple-600 font-medium">Dual Eligible</div>
                  <div className="text-xs font-bold text-purple-800">Yes</div>
                </div>
              )}
              {bbSummary.lis_level && (
                <div className="px-2.5 py-1.5 bg-teal-50 rounded-lg border border-teal-100">
                  <div className="text-[10px] text-teal-600 font-medium">LIS Level</div>
                  <div className="text-xs font-bold text-teal-800">{bbSummary.lis_level}</div>
                </div>
              )}
              {bbSummary.last_er_visit && (
                <div className="px-2.5 py-1.5 bg-red-50 rounded-lg border border-red-100">
                  <div className="text-[10px] text-red-600 font-medium">Last ER Visit</div>
                  <div className="text-xs font-bold text-red-800">
                    {new Date(bbSummary.last_er_visit).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              )}
              {bbSummary.last_hospitalization && (
                <div className="px-2.5 py-1.5 bg-orange-50 rounded-lg border border-orange-100">
                  <div className="text-[10px] text-orange-600 font-medium">Last Hospitalization</div>
                  <div className="text-xs font-bold text-orange-800">
                    {new Date(bbSummary.last_hospitalization).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              )}
              {bbSummary.ytd_drug_spend_cents > 0 && (
                <div className="px-2.5 py-1.5 bg-pink-50 rounded-lg border border-pink-100">
                  <div className="text-[10px] text-pink-600 font-medium">YTD Drug Spend</div>
                  <div className="text-xs font-bold text-pink-800">
                    ${(bbSummary.ytd_drug_spend_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              )}
              {bbSummary.total_providers > 0 && (
                <div className="px-2.5 py-1.5 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="text-[10px] text-slate-600 font-medium">Providers</div>
                  <div className="text-xs font-bold text-slate-800">{bbSummary.total_providers}</div>
                </div>
              )}
            </div>
          )}

          {/* Alert flags */}
          {bbSummary?.active_hospice && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle size={14} className="text-red-500" />
              <span className="text-xs font-medium text-red-700">Active Hospice</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
